import { KEY_POOL, getCloselyRelatedKeys } from '@tunasong/music-lib'
import type { NoteName, Scale } from '@tunasong/schemas'
import type { FC } from 'react'
import { CircleOfFifths } from '../scale/circle-of-fifths.js'
import { CofKeyInfoPanel } from './cof-key-info-panel.js'
import styles from './cof-explore-panel.module.css'

export interface CofExplorePanelProps {
  selectedKey: { name: string; ring: 'major' | 'minor' }
  onSelectKey: (k: { name: string; ring: 'major' | 'minor' }) => void
  onPlay: () => void
}

const slotForKey = (k: { name: string; ring: 'major' | 'minor' }) => {
  if (k.ring === 'major') {
    return KEY_POOL.find(s => s.majorLabels.includes(k.name) || s.canonical === k.name)
  }
  return KEY_POOL.find(s => s.relativeMinor === k.name)
}

/**
 * Derive a Scale for CircleOfFifths from the currently selected wheel key, so
 * the inner Roman-numeral badges (I, IV, V, vi, …) follow the selection rather
 * than staying pinned to C major's diatonic degrees.
 */
const scaleForSelectedKey = (k: { name: string; ring: 'major' | 'minor' }): Scale => ({
  root: k.name as NoteName,
  type: k.ring === 'minor' ? 'minor' : 'major',
})

/**
 * CoF Wheel v1 Explore sub-mode panel — wheel (with closely-related cluster
 * glow + selected-slot stroke) on the left, key-info panel on the right.
 *
 * Spec: docs/superpowers/specs/2026-05-23-cof-wheel-v1-design.md §9
 */
export const CofExplorePanel: FC<CofExplorePanelProps> = props => {
  const { selectedKey, onSelectKey, onPlay } = props
  const slot = slotForKey(selectedKey)
  const cluster = slot ? getCloselyRelatedKeys(slot.canonical) : []
  const scale = scaleForSelectedKey(selectedKey)
  return (
    <div className={styles.layout}>
      <div className={styles.wheel}>
        <CircleOfFifths
          scale={scale}
          selectedKey={selectedKey}
          highlightedKeys={cluster}
          onKeySelect={onSelectKey}
        />
      </div>
      <CofKeyInfoPanel selectedKey={selectedKey} onSelectKey={onSelectKey} onPlay={onPlay} />
    </div>
  )
}
